// src/pages/SoldList.js
import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api } from "../components/Api";
import Loading from "../components/Loading";
import * as XLSX from "xlsx";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
}

export default function SoldList() {
    const [month, setMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });
    const [products, setProducts] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    // Fetch licences et produits vendus
    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Récupère les licences
                const licensesRes = await api.get("/license-cards");
                setLicenses(licensesRes.data);

                // Récupère les produits vendus pour le mois
                let allProducts = [];
                let page = 1;
                let totalPages = 1;
                do {
                    const res = await api.get(`/products?sold=1&month=${month}&page=${page}`);
                    const data = res.data.data || [];
                    allProducts = allProducts.concat(data);
                    totalPages = res.data.last_page || 1;
                    page++;
                } while (page <= totalPages);

                setProducts(allProducts);
            } catch (err) {
                console.error(err);
                alert("Erreur lors de la récupération des produits vendus.");
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [month]);

    // Récupère le nom de la licence d’un produit
    const getLicenseName = (product) => {
        const licenseId = product.cards?.[0]?.license_card_id;
        if (!licenseId) return "";
        const license = licenses.find(l => l.id === licenseId);
        return license ? license.license_card : "";
    };

    // Préparer les données pour Excel
    const prepareSheetData = (data) => {
        const sheetData = data.map(p => ({
            Lot: p.batch || "",
            Référence: p.reference || "",
            Type: p.type?.type_name || "",
            "Sous-type": p.subtype?.subtype_name || "",
            Licence: getLicenseName(p),
            Langue: p.language?.language_name || "",
            Nom: p.name || "",
            Poids: p.weight || "",
            "Prix d'achat": p.buy || 0,
            "Prix de revente": p.sell || 0,
            Bénéfice: p.sell != null && p.buy != null ? parseFloat((p.sell - p.buy).toFixed(2)) : 0,
            "Date de la vente": formatDate(p.updated_at)
        }));

        // Totaux
        const totalAchat = sheetData.reduce((acc, row) => acc + Number(row["Prix d'achat"] || 0), 0);
        const totalVente = sheetData.reduce((acc, row) => acc + Number(row["Prix de revente"] || 0), 0);
        const totalBenef = sheetData.reduce((acc, row) => acc + Number(row["Bénéfice"] || 0), 0);

        sheetData.push({
            Lot: "Total",
            Référence: "",
            Type: "",
            "Sous-type": "",
            Licence: "",
            Langue: "",
            Nom: "",
            Poids: "",
            "Prix d'achat": totalAchat.toFixed(2),
            "Prix de revente": totalVente.toFixed(2),
            Bénéfice: totalBenef.toFixed(2),
            "Date de la vente": ""
        });

        return sheetData;
    };

    const downloadExcel = () => {
        if (!products.length) return alert("Aucun produit à exporter pour ce mois.");

        const wb = XLSX.utils.book_new();

        // Feuille "Tous les produits"
        const wsAll = XLSX.utils.json_to_sheet(prepareSheetData(products));
        XLSX.utils.book_append_sheet(wb, wsAll, "Tous les produits");

        // Feuilles par type
        const types = [...new Set(products.map(p => p.type?.type_name || "Autre"))];
        types.forEach(typeName => {
            const typeData = products.filter(p => (p.type?.type_name || "Autre") === typeName);
            const wsType = XLSX.utils.json_to_sheet(prepareSheetData(typeData));
            XLSX.utils.book_append_sheet(wb, wsType, typeName.substring(0, 31));
        });

        XLSX.writeFile(wb, `${month}_ventes.xlsx`);
    };

    return (
        <div className="container mx-auto p-4 space-y-4">
            <h1 className="text-2xl font-bold dark:text-white">Produits vendus</h1>

            <div className="flex space-x-2 items-center">
                <label>Mois :</label>
                <input
                    type="month"
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    className="border p-2 rounded dark:bg-gray-700 bg-white"
                />
                {localStorage.getItem("role") <= 2 && (
                    <button
                        onClick={downloadExcel}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                    >
                        Télécharger Excel
                    </button>
                )}
            </div>

            {loading ? (
                <Loading message="Chargement des produits..." />
            ) : (
                <table className="w-full border-collapse border text-sm">
                    <thead>
                        <tr>
                            <th className="border px-2 py-1">Lot</th>
                            <th className="border px-2 py-1">Référence</th>
                            <th className="border px-2 py-1">Type</th>
                            <th className="border px-2 py-1">Sous-type</th>
                            <th className="border px-2 py-1">Licence</th>
                            <th className="border px-2 py-1">Langue</th>
                            <th className="border px-2 py-1">Nom</th>
                            <th className="border px-2 py-1">Poids</th>
                            <th className="border px-2 py-1">Prix d'achat</th>
                            <th className="border px-2 py-1">Prix de revente</th>
                            <th className="border px-2 py-1">Bénéfice</th>
                            <th className="border px-2 py-1">Date de la vente</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.length > 0 ? (
                            products.map((p, index) => {
                                const textColor = "dark:text-red-300 text-red-700";
                                const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                                return (
                                    <tr key={p.id} className={`${bgColor} ${textColor}`}>
                                        <td className="border px-2 py-1">{p.batch}</td>
                                        <td className="border px-2 py-1">{p.reference}</td>
                                        <td className="border px-2 py-1">{p.type?.type_name}</td>
                                        <td className="border px-2 py-1">{p.subtype?.subtype_name}</td>
                                        <td className="border px-2 py-1">{getLicenseName(p)}</td>
                                        <td className="border px-2 py-1">{p.language?.language_name}</td>
                                        <td className="border px-2 py-1">{p.name}</td>
                                        <td className="border px-2 py-1">{p.weight}</td>
                                        <td className="border px-2 py-1">{p.buy}</td>
                                        <td className="border px-2 py-1">{p.sell}</td>
                                        <td className="border px-2 py-1">
                                            {p.sell != null && p.buy != null ? (p.sell - p.buy).toFixed(2) : ""}
                                        </td>
                                        <td className="border px-2 py-1">{formatDate(p.updated_at)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={12} className="text-center p-2">
                                    Aucun produit vendu ce mois
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )
            }
        </div >
    );
}
